import { ThemedText } from "@/components/themed-text";
import { BinaryChoice } from "@/components/ui/binary-choice";
import { BrandColors } from "@/constants/theme";
import { useAuth } from "@/contexts/auth-context";
import { useDesktopLayout } from "@/hooks/use-desktop-layout";
import { INSPECTION_TIERS } from "@/lib/inspection-tier-requirements";
import { ServiceType } from "@/lib/validation/booking";
import React from "react";
import { StyleSheet, TouchableOpacity, View } from "react-native";

type Props = {
  serviceType?: string;
  inspectionRequired?: boolean;
  inspectionTier?: string;
  onChange: (values: {
    inspection_required: boolean;
    inspection_tier?: string;
  }) => void;
};

export function isInspectionEligible(serviceType?: string): boolean {
  return [
    ServiceType.VEHICLE_TRANSPORTATION,
    ServiceType.BREAKDOWN_RECOVERY,
    ServiceType.ACCIDENT_RECOVERY,
  ].includes(serviceType as any);
}

export function InspectionAddonCard({
  serviceType,
  inspectionRequired = false,
  inspectionTier,
  onChange,
}: Props) {
  const { theme } = useAuth();
  const desktop = useDesktopLayout();

  if (!isInspectionEligible(serviceType)) {
    return null;
  }

  return (
    <View
      style={[
        styles.card,
        { backgroundColor: theme.background, shadowColor: theme.text },
        desktop && [
          styles.desktopCard,
          { backgroundColor: theme.card, borderColor: theme.border },
        ],
      ]}
    >
      <ThemedText type="subtitle">Vehicle Inspection Add-on</ThemedText>
      <ThemedText style={styles.disclaimer}>
        Optional condition report — not an MOT, warranty or engineering report.
      </ThemedText>

      <BinaryChoice
        label="Add inspection before collection?"
        value={inspectionRequired}
        onValueChange={(value) =>
          onChange({
            inspection_required: value,
            inspection_tier: value ? (inspectionTier ?? "basic") : undefined,
          })
        }
      />

      {inspectionRequired &&
        INSPECTION_TIERS.map((tier) => (
          <TouchableOpacity
            key={tier.value}
            style={[
              styles.tier,
              { backgroundColor: theme.background, borderColor: theme.text },
              inspectionTier === tier.value && styles.tierSelected,
            ]}
            onPress={() =>
              onChange({
                inspection_required: true,
                inspection_tier: tier.value,
              })
            }
          >
            <View style={styles.tierHeader}>
              <ThemedText style={styles.tierLabel}>{tier.label}</ThemedText>
              <ThemedText style={styles.tierFee}>{tier.fee}</ThemedText>
            </View>
            <ThemedText style={styles.tierDescription}>
              {tier.description}
            </ThemedText>
          </TouchableOpacity>
        ))}
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    gap: 12,
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 16,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.05,
    shadowRadius: 12,
    elevation: 4,
  },
  desktopCard: {
    padding: 20,
    borderRadius: 20,
    marginBottom: 0,
    shadowOpacity: 0,
    elevation: 0,
  },
  disclaimer: {
    color: BrandColors.mediumGray,
    fontSize: 13,
  },
  tier: {
    padding: 12,
    borderRadius: 10,
    borderWidth: 1,
  },
  tierSelected: {
    borderColor: BrandColors.primary,
    backgroundColor: `${BrandColors.primary}10`,
  },
  tierHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  tierLabel: {
    fontWeight: "600",
  },
  tierFee: {
    fontWeight: "700",
    color: BrandColors.primary,
  },
  tierDescription: {
    color: BrandColors.mediumGray,
    fontSize: 13,
  },
});
